import {TASK_ACTIONS} from "utils/constants";
import type {TaskGroup} from "./tasksGrouping.function";
import type {MyTask, TaskWrapper} from "types/myTasks";

/**
 * The tasks the user sent on review, shown in the my-tasks grid next to their own.
 *
 * They are the reviewers' tasks, not the user's: the API hands them out without a key, and the
 * only thing the user can do with one is give the review to someone else. So they get a key of
 * their own, apart from any real one, and live in a group of their own at the end of the list.
 * Pure functions, so they can be unit-tested without the grid.
 */

export const ON_REVIEW_GROUP_KEY = '__onReview';

const ON_REVIEW_KEY_PREFIX = 'review:';

/**
 * the review tasks as the list's wrappers - never selected, as they cannot be
 */
export function toReviewWrappers(tasks: MyTask[] | null | undefined): TaskWrapper[] {
    if (!Array.isArray(tasks)) return [];
    return tasks
        .filter(task => task?.uid)
        .map(task => ({
            task: {...task, key: ON_REVIEW_KEY_PREFIX + task.uid, isOnReview: true},
            selected: false
        }));
}

/**
 * whether the user may hand this review over to another reviewer
 */
export function canReassignReview(task: MyTask): boolean {
    const actions: string[] = task?.taskActions ?? task?.actions ?? [];
    return actions.includes(TASK_ACTIONS.REASSIGN);
}

/**
 * the grouped list with the reviews appended as a group of their own, whatever the list is grouped by
 */
export function withReviewGroup(groups: TaskGroup[], reviewWrappers: TaskWrapper[]): TaskGroup[] {
    if (reviewWrappers.length === 0) return groups;
    return [...groups, {
        key: ON_REVIEW_GROUP_KEY,
        labelKey: 'myTasks.onReview.group',
        wrappers: reviewWrappers,
        onReview: true
    }];
}

/**
 * the groups that hold the user's own tasks - the ones stepping from task to task walks through
 */
export function ownTaskGroups(groups: TaskGroup[]): TaskGroup[] {
    return groups.filter(group => !group.onReview);
}
